/**
 * Core Game Configuration
 * Fundamental game settings that rarely change
 */
import {
  CAMPAIGN_V2_DAY_NIGHT_CYCLE_MS,
  CAMPAIGN_V2_DAY_NIGHT_PHASE_EFFECTS,
  CAMPAIGN_V2_DAY_NIGHT_PHASE_WINDOWS,
} from '@towerpower/shared/combat';
import { fontFamily, fontSize } from '../scenes/ui/tokens';

// Display dimensions
export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

// Fixed timestep for deterministic updates
export const TICK_RATE = 60;
export const TICK_MS = 1000 / TICK_RATE;

// Spatial partitioning (for range queries)
export const SPATIAL_GRID_CELL_SIZE = 100;

/**
 * Debug Mode Configuration
 *
 * Debug mode is only available to authenticated admin users. The DBG
 * button in the campaign HUD toggles it at runtime. Non-admin users
 * cannot enable debug mode.
 *
 * Keyboard shortcuts (when debug mode is enabled):
 *   G = Add 1000 gold
 *   K = Kill all enemies
 *   N = Start next wave immediately
 *   I = Toggle invincible mode
 *   H = Toggle hitbox visualization
 *   P = Toggle path visualization
 *   T = Cycle time scale (0.5x, 1x, 2x, 4x)
 *   L = Add 10 lives
 *   1-9 = Skip to wave number
 *   F1 = Toggle debug overlay
 */

// Runtime debug state (can be toggled by admin users only)
let _debugEnabled = false;
let _isAdminUser = false;

export const DebugState = {
  isEnabled: () => _debugEnabled && _isAdminUser,
  /** Returns true if the current user is allowed to toggle debug mode. */
  isAllowed: () => _isAdminUser,
  toggle: () => {
    if (!_isAdminUser) return false;
    _debugEnabled = !_debugEnabled;
    return _debugEnabled;
  },
  set: (value: boolean) => {
    _debugEnabled = _isAdminUser && value;
  },
  /** Called once after auth resolves to grant or revoke debug access. */
  setAdmin: (isAdmin: boolean) => {
    _isAdminUser = isAdmin;
    if (!isAdmin) _debugEnabled = false;
  },
};

/**
 * Text Style Configuration
 * Shared live-combat UI defaults. The clean sans stack and type scale come from the shared
 * UI tokens; the white fills and dark stroke are combat-HUD specific (legibility over busy
 * terrain) and stay local — they carry no interface-palette meaning.
 */
const UI_FONT_FAMILY = fontFamily;

export const TEXT_STYLES = {
  fontFamily: UI_FONT_FAMILY,

  // Pre-defined styles for consistent UI
  heading: {
    fontFamily: UI_FONT_FAMILY,
    fontSize: fontSize.heading,
    color: '#ffffff',
    stroke: '#071015',
    strokeThickness: 2,
  },
  subheading: {
    fontFamily: UI_FONT_FAMILY,
    fontSize: fontSize.subheading,
    color: '#ffffff',
    stroke: '#071015',
    strokeThickness: 1,
  },
  body: {
    fontFamily: UI_FONT_FAMILY,
    fontSize: fontSize.body,
    color: '#ffffff',
    stroke: '#071015',
    strokeThickness: 1,
  },
  small: {
    fontFamily: UI_FONT_FAMILY,
    fontSize: fontSize.small,
    color: '#cccccc',
    stroke: '#071015',
    strokeThickness: 0,
  },
  ui: {
    fontFamily: UI_FONT_FAMILY,
    fontSize: fontSize.ui,
    color: '#ffffff',
    stroke: '#071015',
    strokeThickness: 1,
  },
} as const;

export const DEBUG_CONFIG = {
  // Multipliers when debug mode is enabled
  startingGoldMultiplier: 100,  // 100x starting gold
  passiveIncomeMultiplier: 10,  // 10x passive income

  // Toggleable debug features (can be changed at runtime)
  invincibleBase: false,        // Base can't lose lives
  showHitboxes: false,          // Show enemy/tower collision circles
  showPaths: false,             // Show path waypoints and connections
  showRanges: false,            // Show all tower ranges
  showStats: true,              // Show FPS and entity counts

  // Time control
  timeScales: [0.5, 1, 2, 4],   // Available time scale options
  defaultTimeScale: 1,

  // Quick spawn for testing (press number keys)
  testEnemies: ['footman', 'runner', 'juggernaut', 'warlock', 'wyvern', 'boss_ogre'],
};

/**
 * Day/Night cycle visual settings.
 * A full cycle is: day -> dusk -> night -> dawn -> day.
 */
export const DAY_NIGHT_CONFIG = {
  enabled: true,
  cycleDurationMs: CAMPAIGN_V2_DAY_NIGHT_CYCLE_MS, // Full cycle length (90 seconds)
  dayAlpha: 0,                 // Overlay alpha at peak day
  nightAlpha: 0.34,            // Overlay alpha at peak night
  transitionPulseAlpha: 0.14,  // Brief screen pulse on phase transitions
  phaseWindows: CAMPAIGN_V2_DAY_NIGHT_PHASE_WINDOWS,
  // Gameplay multipliers come from @towerpower/shared (the replay applies the same table); only the labels and colors are web-only.
  phaseEffects: {
    day: {
      ...CAMPAIGN_V2_DAY_NIGHT_PHASE_EFFECTS.day,
      label: 'Day',
      summary: 'Towers fire faster (+15%)',
      uiColor: 0xf2d479,
    },
    dusk: {
      ...CAMPAIGN_V2_DAY_NIGHT_PHASE_EFFECTS.dusk,
      label: 'Dusk',
      summary: 'Enemy advance quickens (+8%)',
      uiColor: 0xf4a261,
    },
    night: {
      ...CAMPAIGN_V2_DAY_NIGHT_PHASE_EFFECTS.night,
      label: 'Night',
      summary: 'Enemies surge (+20%), towers slow (-10%)',
      uiColor: 0x7aa2ff,
    },
    dawn: {
      ...CAMPAIGN_V2_DAY_NIGHT_PHASE_EFFECTS.dawn,
      label: 'Dawn',
      summary: 'Defenders rally (+10%), enemies falter (-5%)',
      uiColor: 0xffbc79,
    },
  },
  biomeTints: {
    grass: 0x0b1a33,
    forest: 0x0a162b,
    castle: 0x101827,
    swamp: 0x101c24,
    volcanic: 0x1e1414,
  },
} as const;
